import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './ComplianceDashboardV2.css';
import { navigate } from 'raviger';
import ComplianceDashboardV2Filter, { FilterSaveState } from './ComplianceDashboardV2Filter';
import ComplianceDashboardCalendar from './ComplianceDashboardCalendar';
import { ComplianceDashboardPayload } from '../../../../backend/complianceDashboardV2';
import {
  useGetComplianceDashboardScoreCards,
  useGetComplianceDashboardCategoryBreakdown,
  useGetComplianceDashboardLocationBreakdown,
  useGetComplianceDashboardOwnerAccountability,
  useGetComplianceDashboardManagerAccountability,
  useGetComplianceDashboardPairsAccountability,
  useGetComplianceDashboardCalendarActivities,
  useGetComplianceDashboardCalendarMonth,
  useGetComplianceDashboardPenaltyMetrics,
} from '../../../../backend/complianceDashboardV2';
import { getUserDetails, hasUserAccess } from '../../../../backend/auth';
import { USER_PRIVILEGES } from '../../UserManagement/Roles/RoleConfiguration';
import { CalendarScheduleItem, CalendarDayData } from './ComplianceDashboardCalendar';
import { toast } from 'react-toastify';
// import { post } from '../../../../backend/request'; // kept for future use
import { downloadFileContent } from '../../../../utils/common';
import { useExportComplianceReport } from '../../../../backend/compliance';
import dayjs from 'dayjs';

// ── Icon assets ───────────────────────────────────────────────────────────────
import iconComplianceRate  from './assets/cd2-icon-compliance-rate.svg';
import iconTotal           from './assets/cd2-icon-total.svg';
import iconUpcoming        from './assets/cd2-icon-upcoming.svg';
import iconNonCompliant    from './assets/cd2-icon-non-compliant.svg';
import iconCompliant       from './assets/cd2-icon-compliant.svg';
import iconRejected        from './assets/cd2-icon-rejected.svg';
import iconPending         from './assets/cd2-icon-pending.svg';
import iconRecalled        from './assets/cd2-icon-recalled.svg';
import iconInfo            from './assets/cd2-icon-info.svg';
import iconCalendar             from './assets/cd2-icon-calendar.svg';
import iconFinancialExposure   from './assets/cd2-icon-financial-exposure.svg';
import iconImprisonment        from './assets/cd2-icon-imprisonment.svg';
import iconLicense             from './assets/cd2-icon-license.svg';

// ── Activity Tabs ─────────────────────────────────────────────────────────────
const ACTIVITY_TABS = [
  'All', 'Appointment', 'Assurance', 'Audit', 'Deposit',
  'Display', 'Internal Compliance', 'Intimation', 'Meeting', 'Policy',
  'Registers', 'Registration', 'Returns', 'Rule',
];

// ── Bar segment tooltip ───────────────────────────────────────────────────────
interface BarTip {
  label: string;
  count: number | string;
  color: string;
  x: number;
  y: number;
}

function BarTooltip({ tip }: { tip: BarTip | null }) {
  const tipRef = useRef<HTMLDivElement>(null);

  // Run synchronously before paint so the user never sees the wrong position.
  useLayoutEffect(() => {
    const el = tipRef.current;
    if (!el || !tip) return;
    const rect = el.getBoundingClientRect();
    // Flip to left of cursor when the right edge would overflow the viewport
    if (rect.right > window.innerWidth - 8) {
      el.style.left = Math.max(8, tip.x - rect.width - 14) + 'px';
    }
    // Push below cursor when the tooltip goes above the top of the viewport
    if (rect.top < 8) {
      el.style.top = tip.y + 14 + 'px';
    }
  }, [tip]);

  if (!tip) return null;
  return (
    <div
      ref={tipRef}
      style={{
        position: 'fixed',
        left: tip.x + 14,
        top: tip.y - 36,
        background: '#1e1e1e',
        color: '#fff',
        padding: '5px 11px',
        borderRadius: 6,
        fontSize: 12,
        fontWeight: 500,
        pointerEvents: 'none',
        zIndex: 9999,
        whiteSpace: 'nowrap',
        boxShadow: '0 3px 10px rgba(0,0,0,0.25)',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
      }}
    >
      <span style={{ width: 9, height: 9, borderRadius: 2, background: tip.color, flexShrink: 0, display: 'inline-block' }} />
      {tip.label}:&nbsp;<strong>{typeof tip.count === 'number' ? tip.count.toLocaleString() : tip.count}</strong>
    </div>
  );
}

// ── Skeleton ───────────────────────────────────────────────────────────────────
function Skeleton({ w, h, radius }: { w?: string | number; h?: string | number; radius?: string | number }) {
  const style: React.CSSProperties = {
    width: w !== undefined ? w : '100%',
    height: h !== undefined ? h : 16,
  };
  if (radius !== undefined) style.borderRadius = radius;
  return <span className="cd2-skeleton" style={style} />;
}

// ── Stat Card ──────────────────────────────────────────────────────────────────
function StatCard({
  iconSrc, value, label, delta, loading, wide, tooltip, tipSide, colorTheme, onClick,
}: {
  iconSrc: string;
  value: string | number;
  label: string;
  delta?: number | null;
  loading?: boolean;
  wide?: boolean;
  tooltip?: string;
  tipSide?: 'right';
  /** Colour theme for the hover background tint — matches the status colour */
  colorTheme?: 'green' | 'blue' | 'amber' | 'red' | 'brown' | 'maroon' | 'orange';
  onClick?: () => void;
}) {
  const [showTip, setShowTip] = useState(false);
  const [tipStyle, setTipStyle] = useState<React.CSSProperties>({});
  const infoRef = useRef<HTMLSpanElement>(null);

  // Use position:fixed computed from getBoundingClientRect so the tooltip
  // escapes every overflow/clip context (overflow-x:auto on cd2-stats-row
  // would otherwise clip a position:absolute tooltip).
  function computeTipStyle() {
    if (infoRef.current) {
      var r = infoRef.current.getBoundingClientRect();
      var tipW = 190;
      var left: number;
      if (tipSide === 'right') {
        left = r.left;
      } else {
        left = r.right - tipW;
      }
      // Clamp to viewport edges with 8 px margin
      if (left < 8) { left = 8; }
      if (left + tipW > window.innerWidth - 8) { left = window.innerWidth - tipW - 8; }
      setTipStyle({
        position: 'fixed',
        left: left,
        top: r.top - 8,     // 8 px gap; tooltip grows upward via translateY(-100%)
        transform: 'translateY(-100%)',
        bottom: 'auto',
        right: 'auto',
        zIndex: 99999,
      });
    }
  }

  return (
    <div
      className={`cd2-stat-card ${wide ? 'cd2-stat-card-wide' : 'cd2-stat-card-std'}${onClick ? ' cd2-stat-card-clickable' : ''}${colorTheme ? ' cd2-theme-' + colorTheme : ''}`}
      onClick={onClick}
    >
      {/* Arrow shown on hover — only for clickable cards */}
      {onClick && (
        <span className="cd2-hover-arrow">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M12 11.3333C12 11.7015 11.7015 12 11.3333 12C10.9651 12 10.6667 11.7015 10.6667 11.3333V6.27604L5.13802 11.8047C4.87767 12.065 4.45566 12.065 4.19531 11.8047C3.93496 11.5443 3.93496 11.1223 4.19531 10.862L9.72396 5.33333H4.66667C4.29848 5.33333 4 5.03486 4 4.66667C4 4.29848 4.29848 4 4.66667 4H11.3333C11.7015 4 12 4.29848 12 4.66667V11.3333Z" fill="#2C2C2C"/>
          </svg>
        </span>
      )}
      <div className="cd2-stat-icon">
        {loading ? <Skeleton w={32} h={32} radius="50%" /> : <img src={iconSrc} alt="" />}
      </div>
      <div className="cd2-stat-body">
        {loading ? (
          <>
            <Skeleton w={60} h={22} />
            <div style={{ marginTop: 6 }}><Skeleton w={80} h={12} /></div>
          </>
        ) : (
          <>
            <div className="cd2-stat-value">
              {value}
              {delta != null && (
                <span style={{ fontSize: 11, marginLeft: 6, color: delta >= 0 ? '#14ae5c' : '#ec221f', fontWeight: 600 }}>
                  {delta >= 0 ? '▲' : '▼'} {Math.abs(delta)}%
                </span>
              )}
            </div>
            <div className="cd2-stat-label-row">
              <span className="cd2-stat-label">{label}</span>
              <span
                ref={infoRef}
                className="cd2-stat-info-wrap"
                onMouseEnter={() => { computeTipStyle(); setShowTip(true); }}
                onMouseLeave={() => setShowTip(false)}
                onClick={(e) => { e.stopPropagation(); computeTipStyle(); setShowTip((v) => !v); }}
              >
                <img src={iconInfo} alt="info" />
                {showTip && tooltip && ReactDOM.createPortal(
                  <div
                    className={`cd2-stat-tip${tipSide === 'right' ? ' cd2-stat-tip-right' : ''}`}
                    style={tipStyle}
                  >
                    {tooltip}
                  </div>,
                  document.body
                )}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Pairs Accountability table (escalation login) ────────────────────────────
// Shows every unique owner+manager combination with status breakdown.
// onCellClick(ownerId, ownerName, managerId, managerName, statusFilter)
//   statusFilter: 'NonCompliant' | 'Approved' | 'Rejected' | 'Submitted' | null (null = Total)
function PairsAccountability({ data, loading, onCellClick }: {
  data: any[];
  loading: boolean;
  onCellClick?: (ownerId: any, ownerName: string, managerId: any, managerName: string, statusFilter: string | null) => void;
}) {
  const SKELETON_COLS = 5; // Non-Compliant, Compliant, Rejected, Approval Pending, Total
  if (loading) {
    return (
      <table className="cd2-table cd2-pairs-accountability-table">
        <thead>
          <tr>
            <th>Owner</th>
            <th>Manager</th>
            <th className="center">Non-Compliant</th>
            <th className="center">Compliant</th>
            <th className="center">Rejected</th>
            <th className="center">Approval Pending</th>
            <th className="center">Total</th>
          </tr>
        </thead>
        <tbody>
          {[...Array(5)].map((_, i) => (
            <tr key={i}>
              <td><Skeleton w={130} h={13} /></td>
              <td><Skeleton w={130} h={13} /></td>
              {[...Array(SKELETON_COLS)].map((__, j) => (
                <td key={j} className="center"><Skeleton w={28} h={16} /></td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  if (!data || data.length === 0) {
    return <div style={{ textAlign: 'center', padding: '32px 0', color: '#9ca3af', fontSize: 13 }}>No Data Available</div>;
  }

  function cell(ownerId: any, ownerName: string, managerId: any, managerName: string, status: string | null, count: any) {
    const clickable = count != null && count !== '—' && onCellClick;
    return {
      onClick: clickable ? () => onCellClick!(ownerId, ownerName, managerId, managerName, status) : undefined,
      style:   clickable ? { cursor: 'pointer' } : undefined,
      title:   clickable
        ? `View ${status || 'all'} activities — ${ownerName} × ${managerName}`
        : undefined,
    };
  }

  return (
    <table className="cd2-table cd2-pairs-accountability-table">
      <thead>
        <tr>
          <th>Owner</th>
          <th>Manager</th>
          <th className="center">Non-Compliant</th>
          <th className="center">Compliant</th>
          <th className="center">Rejected</th>
          <th className="center">Approval Pending</th>
          <th className="center">Total</th>
        </tr>
      </thead>
      <tbody>
        {data.map((row: any, idx: number) => (
          <tr key={`${row.owner_id}-${row.manager_id}-${idx}`}>
            <td className="cd2-pair-name-cell">{row.owner_name}</td>
            <td className="cd2-pair-name-cell">{row.manager_name}</td>
            <td className="center cd2-count-red"
              {...cell(row.owner_id, row.owner_name, row.manager_id, row.manager_name, 'NonCompliant', row.non_compliant)}>
              {row.non_compliant != null ? row.non_compliant : 0}
            </td>
            <td className="center cd2-count-green"
              {...cell(row.owner_id, row.owner_name, row.manager_id, row.manager_name, 'Approved', row.compliant)}>
              {row.compliant != null ? row.compliant : 0}
            </td>
            <td className="center cd2-count-orange"
              {...cell(row.owner_id, row.owner_name, row.manager_id, row.manager_name, 'Rejected', row.rejected)}>
              {row.rejected != null ? row.rejected : 0}
            </td>
            <td className="center cd2-count-dark"
              {...cell(row.owner_id, row.owner_name, row.manager_id, row.manager_name, 'Submitted', row.approval_pending)}>
              {row.approval_pending != null ? row.approval_pending : 0}
            </td>
            <td className="center cd2-count-blue"
              {...cell(row.owner_id, row.owner_name, row.manager_id, row.manager_name, null, row.total)}>
              {row.total != null ? row.total : 0}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Stacked bar chart (By Category) ───────────────────────────────────────────
function StackedBarChart({ data, loading, onSegClick }: {
  data: any[];
  loading: boolean;
  onSegClick?: (activityType: string, status: string) => void;
}) {
  const [tip, setTip] = useState<BarTip | null>(null);

  if (loading) {
    return (
      <div>
        {[...Array(5)].map((_, i) => (
          <div key={i} className="cd2-bar-row" style={{ marginBottom: 12 }}>
            <div className="cd2-bar-row-label"><Skeleton w={90} h={12} /></div>
            <div style={{ flex: 1 }}><Skeleton h={24} /></div>
            <div className="cd2-bar-count"><Skeleton w={30} h={12} /></div>
          </div>
        ))}
      </div>
    );
  }
  if (data.length === 0) {
    return <div style={{ textAlign: 'center', padding: '32px 0', color: '#9ca3af', fontSize: 13 }}>No Data Available</div>;
  }

  function onSeg(e: React.MouseEvent, label: string, count: number | string, color: string) {
    setTip({ label, count, color, x: e.clientX, y: e.clientY });
  }
  function moveSeg(e: React.MouseEvent) {
    setTip((prev) => prev ? { ...prev, x: e.clientX, y: e.clientY } : null);
  }

  return (
    <div onMouseLeave={() => setTip(null)}>
      <BarTooltip tip={tip} />
      {/* Column header — clarifies the total-activity count on the right */}
      <div className="cd2-bar-row" style={{ marginBottom: 6 }}>
        <div className="cd2-bar-row-label" />
        <div style={{ flex: 1 }} />
        <div className="cd2-bar-count" style={{ fontWeight: 600, fontSize: 11, color: '#6b7280', textAlign: 'right' }}
          title="Total Activity Count">
          Total
        </div>
      </div>
      {data.map((row) => {
        const nc   = row.non_compliant_pct || 0;
        const ov   = row.due_pct || 0;
        const co   = row.compliant_pct || 0;
        // Normalise widths so all three segments always fill 100% of the track
        const sum  = nc + ov + co;
        const ncW  = sum > 0 ? (nc / sum) * 100 : 0;
        const ovW  = sum > 0 ? (ov / sum) * 100 : 0;
        const coW  = sum > 0 ? 100 - ncW - ovW : 0;
        const ncCount = row.non_compliant_count != null ? row.non_compliant_count : nc + '%';
        const ovCount = row.due_count           != null ? row.due_count           : ov + '%';
        const coCount = row.compliant_count     != null ? row.compliant_count     : co + '%';
        // When all three percentages are zero but the row has a non-zero total,
        // the activities are in "other" statuses (Rejected, Approval Pending, Recalled).
        // Render a grey ghost bar so the row isn't blank.
        const hasOnlyOther = sum === 0 && (row.total || 0) > 0;
        return (
          <div className="cd2-bar-row" key={row.activity_type}>
            <div className="cd2-bar-row-label" title={row.activity_type}>{row.activity_type}</div>
            <div className="cd2-bar-track">
              {nc > 0 && (
                <div className="cd2-bar-seg nc" style={{ width: `${ncW}%` }}
                  onMouseEnter={(e) => onSeg(e, 'Non-Compliant', ncCount, '#ec221f')}
                  onMouseMove={moveSeg}
                  onClick={() => onSegClick && onSegClick(row.activity_type, 'NonCompliant')}>
                  {nc >= 8 ? `${nc}%` : ''}
                </div>
              )}
              {ov > 0 && (
                <div className="cd2-bar-seg ov" style={{ width: `${ovW}%` }}
                  onMouseEnter={(e) => onSeg(e, 'Upcoming', ovCount, '#e5a000')}
                  onMouseMove={moveSeg}
                  onClick={() => onSegClick && onSegClick(row.activity_type, 'Due')}>
                  {ov >= 8 ? `${ov}%` : ''}
                </div>
              )}
              {co > 0 && (
                <div className="cd2-bar-seg co" style={{ width: `${coW}%` }}
                  onMouseEnter={(e) => onSeg(e, 'Compliant', coCount, '#14ae5c')}
                  onMouseMove={moveSeg}
                  onClick={() => onSegClick && onSegClick(row.activity_type, 'Approved')}>
                  {co >= 8 ? `${co}%` : ''}
                </div>
              )}
              {hasOnlyOther && (
                <div
                  className="cd2-bar-seg other"
                  onMouseEnter={(e) => onSeg(e, 'Other Activities', row.total, '#6b7280')}
                  onMouseMove={moveSeg}
                  onClick={() => onSegClick && onSegClick(row.activity_type, 'Other')}>
                </div>
              )}
            </div>
            <div className="cd2-bar-count">{(row.total || 0).toLocaleString()}</div>
          </div>
        );
      })}
    </div>
  );
}

// ── Location chart — stacked bars (By Location) ───────────────────────────────
function LocationChart({ data, loading, onSegClick }: {
  data: any[];
  loading: boolean;
  onSegClick?: (locationId: number, locationName: string, status: string) => void;
}) {
  const [tip, setTip] = useState<BarTip | null>(null);

  if (loading) {
    return (
      <div>
        {[...Array(5)].map((_, i) => (
          <div key={i} className="cd2-bar-row" style={{ marginBottom: 12 }}>
            <div className="cd2-bar-row-label"><Skeleton w={90} h={12} /></div>
            <div style={{ flex: 1 }}><Skeleton h={24} /></div>
            <div className="cd2-bar-count"><Skeleton w={30} h={12} /></div>
          </div>
        ))}
      </div>
    );
  }
  if (data.length === 0) {
    return <div style={{ textAlign: 'center', padding: '32px 0', color: '#9ca3af', fontSize: 13 }}>No Data Available</div>;
  }

  function onSeg(e: React.MouseEvent, label: string, count: number | string, color: string) {
    setTip({ label, count, color, x: e.clientX, y: e.clientY });
  }
  function moveSeg(e: React.MouseEvent) {
    setTip((prev) => prev ? { ...prev, x: e.clientX, y: e.clientY } : null);
  }

  return (
    <div onMouseLeave={() => setTip(null)}>
      <BarTooltip tip={tip} />
      {/* Column header — clarifies the total-activity count on the right */}
      <div className="cd2-bar-row" style={{ marginBottom: 6 }}>
        <div className="cd2-bar-row-label" />
        <div style={{ flex: 1 }} />
        <div className="cd2-bar-count" style={{ fontWeight: 600, fontSize: 11, color: '#6b7280', textAlign: 'right' }}
          title="Total Activity Count">
          Total
        </div>
      </div>
      {data.map((row) => {
        const nc   = row.non_compliant_pct || 0;
        const ov   = row.due_pct || 0;
        const co   = row.compliant_pct || 0;
        // Normalise widths so all three segments always fill 100% of the track
        const sum  = nc + ov + co;
        const ncW  = sum > 0 ? (nc / sum) * 100 : 0;
        const ovW  = sum > 0 ? (ov / sum) * 100 : 0;
        const coW  = sum > 0 ? 100 - ncW - ovW : 0;
        const ncCount = row.non_compliant_count != null ? row.non_compliant_count : nc + '%';
        const ovCount = row.due_count           != null ? row.due_count           : ov + '%';
        const coCount = row.compliant_count     != null ? row.compliant_count     : co + '%';
        // When all three percentages are zero but the row has a non-zero total,
        // the activities are in "other" statuses (Rejected, Approval Pending, Recalled).
        // Render a grey ghost bar so the row isn't blank.
        const hasOnlyOther = sum === 0 && (row.total || 0) > 0;
        return (
          <div className="cd2-bar-row" key={row.location_id}>
            <div className="cd2-bar-row-label" title={row.location_name}>{row.location_name}</div>
            <div className="cd2-bar-track">
              {nc > 0 && (
                <div className="cd2-bar-seg nc" style={{ width: `${ncW}%` }}
                  onMouseEnter={(e) => onSeg(e, 'Non-Compliant', ncCount, '#ec221f')}
                  onMouseMove={moveSeg}
                  onClick={() => onSegClick && onSegClick(row.location_id, row.location_name, 'NonCompliant')}>
                  {nc >= 8 ? `${nc}%` : ''}
                </div>
              )}
              {ov > 0 && (
                <div className="cd2-bar-seg ov" style={{ width: `${ovW}%` }}
                  onMouseEnter={(e) => onSeg(e, 'Upcoming', ovCount, '#e5a000')}
                  onMouseMove={moveSeg}
                  onClick={() => onSegClick && onSegClick(row.location_id, row.location_name, 'Due')}>
                  {ov >= 8 ? `${ov}%` : ''}
                </div>
              )}
              {co > 0 && (
                <div className="cd2-bar-seg co" style={{ width: `${coW}%` }}
                  onMouseEnter={(e) => onSeg(e, 'Compliant', coCount, '#14ae5c')}
                  onMouseMove={moveSeg}
                  onClick={() => onSegClick && onSegClick(row.location_id, row.location_name, 'Approved')}>
                  {co >= 8 ? `${co}%` : ''}
                </div>
              )}
              {hasOnlyOther && (
                <div
                  className="cd2-bar-seg other"
                  onMouseEnter={(e) => onSeg(e, 'Other Activities', row.total, '#6b7280')}
                  onMouseMove={moveSeg}
                  onClick={() => onSegClick && onSegClick(row.location_id, row.location_name, 'Other')}>
                </div>
              )}
            </div>
            <div className="cd2-bar-count">{(row.total || 0).toLocaleString()}</div>
          </div>
        );
      })}
    </div>
  );
}

// ── Owner Accountability table ─────────────────────────────────────────────────
// onCellClick(ownerId, ownerName, statusFilter)
//   statusFilter: 'NonCompliant' | 'Approved' | 'Rejected' | 'Submitted' | null (null = Total)
function OwnerAccountability({ data, loading, onCellClick }: {
  data: any[];
  loading: boolean;
  onCellClick?: (ownerId: number, ownerName: string, statusFilter: string | null) => void;
}) {
  const SKELETON_COLS = 5; // Non-Compliant, Compliant, Rejected, Approval Pending, Total
  if (loading) {
    return (
      <table className="cd2-table cd2-owner-accountability-table">
        <thead>
          <tr>
            <th>Owner</th>
            <th className="center">Non-Compliant</th>
            <th className="center">Compliant</th>
            <th className="center">Rejected</th>
            <th className="center">Approval Pending</th>
            <th className="center">Total</th>
          </tr>
        </thead>
        <tbody>
          {[...Array(5)].map((_, i) => (
            <tr key={i}>
              <td><Skeleton w={140} h={13} /></td>
              {[...Array(SKELETON_COLS)].map((__, j) => (
                <td key={j} className="center"><Skeleton w={28} h={16} /></td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  if (data.length === 0) {
    return <div style={{ textAlign: 'center', padding: '32px 0', color: '#9ca3af', fontSize: 13 }}>No Data Available</div>;
  }

  // Cell click helper — skips "—" (null/undefined counts)
  function cell(ownerId: number, ownerName: string, status: string | null, count: any) {
    const clickable = count != null && count !== '—' && onCellClick;
    return {
      onClick: clickable ? () => onCellClick(ownerId, ownerName, status) : undefined,
      style: clickable ? { cursor: 'pointer' } : undefined,
      title: clickable ? ('View ' + (status || 'all') + ' activities for ' + ownerName) : undefined,
    };
  }

  return (
    <table className="cd2-table cd2-owner-accountability-table">
      <thead>
        <tr>
          <th>Owner</th>
          <th className="center">Non-Compliant</th>
          <th className="center">Compliant</th>
          <th className="center">Rejected</th>
          <th className="center">Approval Pending</th>
          <th className="center">Total</th>
        </tr>
      </thead>
      <tbody>
        {data.map((row) => {
          const compliant      = row.compliant        != null ? row.compliant        : row.approved;
          const rejected       = row.rejected        != null ? row.rejected        : null;
          // approval_pending is the canonical field; fall back to older aliases.
          // Default to 0 (not null) so a genuine zero shows "0" not "—".
          const pendingReview  = row.approval_pending != null ? row.approval_pending
                               : row.pending_review  != null ? row.pending_review
                               : row.pending         != null ? row.pending
                               : 0;
          return (
            <tr key={row.owner_id}>
              <td className="cd2-owner-name-cell">{row.owner_name}</td>
              <td className="center cd2-count-red"
                {...cell(row.owner_id, row.owner_name, 'NonCompliant', row.non_compliant)}>
                {row.non_compliant}
              </td>
              <td className="center cd2-count-green"
                {...cell(row.owner_id, row.owner_name, 'Approved', compliant)}>
                {compliant}
              </td>
              <td className="center cd2-count-orange"
                {...cell(row.owner_id, row.owner_name, 'Rejected', rejected)}>
                {rejected}
              </td>
              <td className="center cd2-count-dark"
                {...cell(row.owner_id, row.owner_name, 'Submitted', pendingReview)}>
                {pendingReview}
              </td>
              <td className="center cd2-count-blue"
                {...cell(row.owner_id, row.owner_name, null, row.total)}>
                {row.total}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ── Manager Accountability table (owner login — grouped by manager) ────────────
// onCellClick(managerId, managerName, statusFilter)
//   statusFilter: 'NonCompliant' | 'Approved' | 'Rejected' | 'Submitted' | null
function ManagerAccountability({ data, loading, onCellClick }: {
  data: any[];
  loading: boolean;
  onCellClick?: (managerId: number, managerName: string, statusFilter: string | null) => void;
}) {
  const SKELETON_COLS = 5;
  if (loading) {
    return (
      <table className="cd2-table cd2-owner-accountability-table">
        <thead>
          <tr>
            <th>Manager</th>
            <th className="center">Non-Compliant</th>
            <th className="center">Compliant</th>
            <th className="center">Rejected</th>
            <th className="center">Approval Pending</th>
            <th className="center">Total</th>
          </tr>
        </thead>
        <tbody>
          {[...Array(5)].map(function(_, i) {
            return (
              <tr key={i}>
                <td><Skeleton w={140} h={13} /></td>
                {[...Array(SKELETON_COLS)].map(function(__, j) {
                  return <td key={j} className="center"><Skeleton w={28} h={16} /></td>;
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }
  if (data.length === 0) {
    return <div style={{ textAlign: 'center', padding: '32px 0', color: '#9ca3af', fontSize: 13 }}>No Data Available</div>;
  }

  function cell(managerId: number, managerName: string, status: string | null, count: any) {
    var clickable = count != null && count !== '—' && onCellClick;
    return {
      onClick:  clickable ? function() { if (onCellClick) onCellClick(managerId, managerName, status); } : undefined,
      style:    clickable ? { cursor: 'pointer' } : undefined,
      title:    clickable ? ('View ' + (status || 'all') + ' activities for ' + managerName) : undefined,
    };
  }

  return (
    <table className="cd2-table cd2-owner-accountability-table">
      <thead>
        <tr>
          <th>Manager</th>
          <th className="center">Non-Compliant</th>
          <th className="center">Compliant</th>
          <th className="center">Rejected</th>
          <th className="center">Approval Pending</th>
          <th className="center">Total</th>
        </tr>
      </thead>
      <tbody>
        {data.map(function(row) {
          var compliant = row.approved  != null ? row.approved  : null;
          var rejected  = row.rejected  != null ? row.rejected  : null;
          // approval_pending is the new canonical field; fall back to legacy `pending`.
          // Default to 0 so a genuine zero always shows "0", never "—".
          var pending   = row.approval_pending != null ? row.approval_pending
                        : row.pending          != null ? row.pending
                        : 0;
          return (
            <tr key={row.manager_id}>
              <td className="cd2-owner-name-cell">
                <div>{row.manager_name}</div>
                {row.manager_role && (
                  <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400, marginTop: 2 }}>
                    {row.manager_role}
                  </div>
                )}
              </td>
              <td className="center cd2-count-red"
                {...cell(row.manager_id, row.manager_name, 'NonCompliant', row.non_compliant)}>
                {row.non_compliant}
              </td>
              <td className="center cd2-count-green"
                {...cell(row.manager_id, row.manager_name, 'Approved', compliant)}>
                {compliant != null ? compliant : 0}
              </td>
              <td className="center cd2-count-orange"
                {...cell(row.manager_id, row.manager_name, 'Rejected', rejected)}>
                {rejected != null ? rejected : 0}
              </td>
              <td className="center cd2-count-dark"
                {...cell(row.manager_id, row.manager_name, 'Submitted', pending)}>
                {pending}
              </td>
              <td className="center cd2-count-blue"
                {...cell(row.manager_id, row.manager_name, null, row.total)}>
                {row.total}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ── SessionStorage key for filter-state persistence ───────────────────────────
const CD2_SESSION_KEY = 'cd2DashboardState';

// ── Main Component ─────────────────────────────────────────────────────────────
export default function ComplianceDashboardV2() {
  // ── Read any saved state from sessionStorage on the very first render ──────
  // This runs before useState initialisers so we can seed state from saved data.
  const [_savedSession] = useState<any>(() => {
    try {
      const raw = sessionStorage.getItem(CD2_SESSION_KEY);
      if (raw) {
        sessionStorage.removeItem(CD2_SESSION_KEY); // consume immediately
        return JSON.parse(raw);
      }
    } catch (_) { /* ignore parse errors */ }
    return null;
  });

  const [apiPayload, setApiPayload] = useState<ComplianceDashboardPayload | null>(
    (_savedSession && _savedSession.apiPayload) || null
  );
  const [selectedTab,  setSelectedTab]  = useState(
    (_savedSession && _savedSession.selectedTab) || 'All'
  );
  const [chartView, setChartView] = useState<'category' | 'location'>(
    (_savedSession && _savedSession.chartView) || 'category'
  );
  const { exportReport, exporting } = useExportComplianceReport(
    (response: any) => {
      downloadFileContent({
        name: `Compliance_Report_${new Date().toLocaleDateString('en-IN').replace(/\//g, '-')}.pdf`,
        type: (response.headers && response.headers['content-type']) || 'application/pdf',
        content: response.data,
      });
    },
    () => toast.error('Report generation failed. Please try again.'),
  );
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarDate, setCalendarDate] = useState<string>(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [calYear,  setCalYear]  = useState(() => new Date().getFullYear());
  const [calMonth, setCalMonth] = useState(() => new Date().getMonth() + 1);

  // ── Filter-state persistence ──────────────────────────────────────────────
  // The filter component keeps its own display state (company dropdowns, time
  // range, etc.) and calls onStateChange whenever anything changes so we always
  // have the latest snapshot in this ref — without causing a parent re-render.
  const filterStateRef = useRef<FilterSaveState | null>(null);

  // Call this right before every navigate('/complianceManagement/list', …) so
  // the filter state is preserved for the back-button journey.
  function saveDashboardState() {
    if (!apiPayload) return; // nothing meaningful to save yet
    try {
      sessionStorage.setItem(CD2_SESSION_KEY, JSON.stringify({
        filterState: filterStateRef.current,
        apiPayload,
        selectedTab,
        chartView,
      }));
    } catch (_) { /* quota exceeded — silently skip */ }
  }

  // ── User role scope ───────────────────────────────────────────────────────
  const isOwner      = hasUserAccess(USER_PRIVILEGES.OWNER_DASHBOARD);
  const isManager    = hasUserAccess(USER_PRIVILEGES.MANAGER_DASHBOARD);
  const isEscalation = hasUserAccess(USER_PRIVILEGES.ESCALATION_DASHBOARD);
  const loginUser: number = getUserDetails().user_id;

  // For escalation role, don't fire any API until a company is explicitly selected
  // (user may have no company assigned — without company_id the backend returns
  // data for ALL companies, which would leak cross-company data).
  const escalationReady = !isEscalation || (apiPayload !== null && apiPayload.company_id != null && apiPayload.company_id.length > 0);
  const enabled = apiPayload !== null && escalationReady;
  const safePayload: ComplianceDashboardPayload = apiPayload || {};

  // Merge activity type tab + auto-scope by logged-in user role.
  // owner_id_eq / manager_id_eq tell the backend to restrict results to
  // activities assigned to the currently logged-in owner or manager.
  const basePayload: ComplianceDashboardPayload = selectedTab === 'All'
    ? safePayload
    : { ...safePayload, activity_type: selectedTab };

  // owner_id / manager_id are the top-level params the backend dashboard service
  // reads directly — owner login always scopes to [loginUser]; manager login
  // adds manager_id:[loginUser] while preserving any owner_id from More Filters.
  const effectivePayload: ComplianceDashboardPayload = isOwner
    ? { ...basePayload, owner_id: [loginUser] }
    : isManager
      ? { ...basePayload, manager_id: [loginUser] }
      : basePayload;

  // ── API calls ─────────────────────────────────────────────────────────────
  const { scoreCards, isFetching: fetchingScoreCards } =
    useGetComplianceDashboardScoreCards(effectivePayload, enabled);

  const { categories, isFetching: fetchingCategories } =
    useGetComplianceDashboardCategoryBreakdown(effectivePayload, enabled);

  const { locations, isFetching: fetchingLocations } =
    useGetComplianceDashboardLocationBreakdown(effectivePayload, enabled);

  // Manager login → show owner accountability table
  // Owner  login → show manager accountability table
  // Escalation login → show combined pair accountability (owner × manager combinations)
  const { owners, isFetching: fetchingOwners } =
    useGetComplianceDashboardOwnerAccountability(effectivePayload, enabled && isManager);

  const { managers, isFetching: fetchingManagers } =
    useGetComplianceDashboardManagerAccountability(effectivePayload, enabled && isOwner);

  const { pairs, isFetching: fetchingPairs } =
    useGetComplianceDashboardPairsAccountability(effectivePayload, enabled && isEscalation);

  const { penaltyMetrics, isFetching: fetchingPenalty } =
    useGetComplianceDashboardPenaltyMetrics(effectivePayload, enabled);

  // Calendar API — strip financial_year / quarter / month_year; only pass filter IDs
  // Also forward the user scope so calendar results stay scoped to the logged-in user
  const calendarBasePayload: ComplianceDashboardPayload = {
    company_id:           safePayload.company_id,
    associate_company_id: safePayload.associate_company_id,
    state_id:             safePayload.state_id,
    location_id:          safePayload.location_id,
    vertical_id:          safePayload.vertical_id,
    department_id:        safePayload.department_id,
    owner_id:             isOwner   ? [loginUser] : safePayload.owner_id,
    manager_id:           isManager ? [loginUser] : safePayload.manager_id,
  };
  const { items: calRawItems, totalCount: calTotal, isFetching: fetchingCal } =
    useGetComplianceDashboardCalendarActivities(calendarBasePayload, calendarDate, calendarOpen);

  // Month-level data — colours every date cell in the calendar grid
  const { monthDates, isFetching: fetchingCalMonth } =
    useGetComplianceDashboardCalendarMonth(calendarBasePayload, calYear, calMonth, calendarOpen);

  const calDayData: CalendarDayData[] = monthDates.map((d) => ({
    date:   d.date,
    status: d.dominant_status as CalendarDayData['status'],
    total:  d.total,
  }));

  // Map raw_status from API → internal CalendarScheduleItem status key
  function mapRawStatus(raw: string): CalendarScheduleItem['status'] {
    if (raw === 'NonCompliant') return 'non_compliant';
    if (raw === 'Due')          return 'upcoming';
    if (raw === 'Submitted')    return 'approval_pending';
    if (raw === 'Rejected')     return 'rejected';
    return 'upcoming';
  }
  const calendarItems: CalendarScheduleItem[] = calRawItems.map((item: any) => ({
    id:     item.compliance_id,
    title:  item.activity_name,
    status: mapRawStatus(item.raw_status),
  }));

  // ── Penalty formatters ────────────────────────────────────────────────────
  function formatPenaltyAmount(amount: number): string {
    if (amount >= 10000000) {
      return '₹' + (amount / 10000000).toFixed(2) + ' Cr';
    }
    if (amount >= 100000) {
      return '₹' + (amount / 100000).toFixed(2) + ' L';
    }
    if (amount >= 1000) {
      return '₹' + (amount / 1000).toFixed(1) + ' K';
    }
    return '₹' + Math.round(amount);
  }
  function formatPenaltyAvg(amount: number): string {
    return 'Avg ₹' + Math.round(amount).toLocaleString('en-IN') + ' / activity';
  }

  // ── Handlers ──────────────────────────────────────────────────────────────
  function handleFilterApply(payload: ComplianceDashboardPayload) {
    setApiPayload(payload);
    setSelectedTab('All'); // Reset activity tab on filter change
  }

  function todayDateString(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  function resetCalendarToToday() {
    const now = new Date();
    setCalYear(now.getFullYear());
    setCalMonth(now.getMonth() + 1);
    setCalendarDate(todayDateString());
  }

  function handleCalMonthChange(year: number, month: number) {
    setCalYear(year);
    setCalMonth(month);
    // Reset selected date to the 1st of the new month so the schedule
    // section clears and no stale date from a previous month is shown.
    const mm = String(month).padStart(2, '0');
    setCalendarDate(`${year}-${mm}-01`);
  }

  // ── Penalty card navigation ───────────────────────────────────────────────
  function handlePenaltyClick(penaltyType: 'financial_exposure' | 'imprisonment_risk' | 'license_revocation') {
    if (!apiPayload) return;
    saveDashboardState();
    navigate('/complianceManagement/list', {
      state: {
        from_penalty:      penaltyType,
        dashboard_payload: effectivePayload,
      },
    });
  }

  // ── KPI stat card navigation ───────────────────────────────────────────────
  // status_filter: null = no status filter (Total Activities = all statuses)
  function handleKpiClick(label: string, statusFilter: string | null) {
    if (!apiPayload) return;
    saveDashboardState();
    navigate('/complianceManagement/list', {
      state: {
        from_kpi:         label,
        status_filter:    statusFilter,
        dashboard_payload: effectivePayload,
      },
    });
  }

  // ── Owner Accountability cell navigation (manager login) ─────────────────
  // statusFilter: null = no status filter (Total column)
  function handleOwnerCellClick(ownerId: number, ownerName: string, statusFilter: string | null) {
    if (!apiPayload) return;
    saveDashboardState();
    navigate('/complianceManagement/list', {
      state: {
        from_owner:           true,
        owner_id:             ownerId,
        owner_name:           ownerName,
        status_filter:        statusFilter,
        dashboard_payload:    effectivePayload,
      },
    });
  }

  // ── Manager Accountability cell navigation (owner login) ──────────────────
  function handleManagerCellClick(managerId: number, managerName: string, statusFilter: string | null) {
    if (!apiPayload) return;
    saveDashboardState();
    navigate('/complianceManagement/list', {
      state: {
        from_manager:      true,
        manager_id:        managerId,
        manager_name:      managerName,
        status_filter:     statusFilter,
        dashboard_payload: effectivePayload,
      },
    });
  }

  // ── Pairs Accountability cell navigation (escalation login) ──────────────
  // Applies both owner_id and manager_id so the listing is scoped to the exact pair.
  function handlePairCellClick(ownerId: any, ownerName: string, managerId: any, managerName: string, statusFilter: string | null) {
    if (!apiPayload) return;
    saveDashboardState();
    navigate('/complianceManagement/list', {
      state: {
        from_pair:         true,
        owner_id:          ownerId,
        owner_name:        ownerName,
        manager_id:        managerId,
        manager_name:      managerName,
        status_filter:     statusFilter,
        dashboard_payload: effectivePayload,
      },
    });
  }

  // ── Calendar "View More" navigation ──────────────────────────────────────────
  // Navigates to listing page filtered by the selected calendar date + all
  // current dashboard filters; the date is passed separately so the listing
  // page can pre-populate the Due Date fields in More Filters.
  function handleCalViewMore() {
    saveDashboardState();
    navigate('/complianceManagement/list', {
      state: {
        from_calendar:     true,
        calendar_date:     calendarDate,
        dashboard_payload: effectivePayload,
      },
    });
  }

  // ── Chart segment navigation ───────────────────────────────────────────────
  // By Category: click a coloured segment → listing filtered by activity type + status
  function handleCategorySegClick(activityType: string, status: string) {
    if (!apiPayload) return;
    saveDashboardState();
    navigate('/complianceManagement/list', {
      state: {
        from_chart:           'category',
        chart_activity_type:  activityType,
        chart_status:         status,
        dashboard_payload:    effectivePayload,
      },
    });
  }

  // By Location: click a coloured segment → listing filtered by location + status
  function handleLocationSegClick(locationId: number, locationName: string, status: string) {
    if (!apiPayload) return;
    saveDashboardState();
    navigate('/complianceManagement/list', {
      state: {
        from_chart:           'location',
        chart_location_id:    locationId,
        chart_location_name:  locationName,
        chart_status:         status,
        dashboard_payload:    effectivePayload,
      },
    });
  }


  // ── Report export ─────────────────────────────────────────────────────────
  // Company + Associate Company are mandatory — all other global filters flow through.
  // month_year ("YYYY-MM") is not supported by the report API — convert to
  // filters[from_date]/filters[to_date] (old-style, still accepted by the endpoint).
  // year_eq (Full Year) maps to financial_year string e.g. "2025-2026".
  function handleExport() {
    if (!apiPayload) return;
    const hasCompany = apiPayload.company_id && apiPayload.company_id.length > 0;
    const hasAC      = apiPayload.associate_company_id && apiPayload.associate_company_id.length > 0;
    if (!hasCompany) {
      toast.warn('Please select a Company in the global filters before generating the report.');
      return;
    }
    if (!hasAC) {
      toast.warn('Please select an Associate Company in the global filters before generating the report.');
      return;
    }

    // Build clean report payload
    const { month_year, ...rest } = apiPayload as any;
    const reportPayload: any = { ...rest };

    if (month_year) {
      // Convert "YYYY-MM" → filters[from_date] (1st) + filters[to_date] (last day of month)
      // The report API does not accept month_year directly.
      const start = dayjs(`${month_year}-01`);
      reportPayload.filters = {
        from_date: start.startOf('month').local().format(),
        to_date:   start.endOf('month').local().format(),
      };
    }
    // year_eq (Full Year) is passed through as-is — e.g. year_eq: 2026

    exportReport(reportPayload);
  }

  const sc = scoreCards;

  return (
    <div className="cd2-outer">
    <div className="cd2-wrapper">
      {/* ── Header ── */}
      <div className="cd2-header">
        <div>
          <h1 className="cd2-header-title">Compliance Dashboard</h1>
          <p className="cd2-header-subtitle">
            Track regulatory compliance, manage audits, and stay ahead of policy deadlines.
          </p>
        </div>
        <div className="cd2-header-actions">
          <button className="cd2-export-btn" onClick={handleExport} disabled={exporting || !enabled}>
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2.667 10.667v1.333A1.333 1.333 0 0 0 4 13.333h8a1.333 1.333 0 0 0 1.333-1.333v-1.333M10.667 8 8 10.667M8 10.667 5.333 8M8 10.667V2.667" />
            </svg>
            {exporting ? 'Generating…' : 'Report'}
          </button>
          <button
            className={`cd2-calendar-btn${calendarOpen ? ' active' : ''}`}
            onClick={() => {
              const opening = !calendarOpen;
              if (opening) resetCalendarToToday();
              setCalendarOpen(opening);
            }}
          >
            <img src={iconCalendar} alt="calendar" />
            Calendar View
          </button>
        </div>
      </div>

      {/* ── Filter Card ── */}
      <ComplianceDashboardV2Filter
        onApply={handleFilterApply}
        selectedTab={selectedTab}
        onTabChange={setSelectedTab}
        initialState={(_savedSession && _savedSession.filterState) || null}
        onStateChange={(s) => { filterStateRef.current = s; }}
      />

      {/* ── Penalties Section ── */}
      <div className="cd2-penalty-section">

        {/* Header */}
        <div className="cd2-penalty-section-header">
          <span className="cd2-penalty-section-title">Penalties</span>
          <span className="cd2-penalty-section-subtitle">
            {'Statutory exposure from the '}
            {sc && sc.non_compliant && sc.non_compliant.count
              ? sc.non_compliant.count.toLocaleString('en-IN')
              : 0}
            {' non-compliant activities across all categories'}
          </span>
        </div>

        {/* 3 metric cards */}
        <div className="cd2-penalty-row">
          {!escalationReady ? (
            /* No company assigned — show static empty cards instead of shimmers */
            <React.Fragment>
              <div className="cd2-penalty-card cd2-penalty-card-financial">
                <div className="cd2-penalty-header">
                  <div className="cd2-penalty-left">
                    <div className="cd2-penalty-badge" style={{ background: '#7c3aed' }}>
                      <img src={iconFinancialExposure} alt="" />
                    </div>
                    <span className="cd2-penalty-value">0</span>
                  </div>
                </div>
                <div className="cd2-penalty-bottom">
                  <span className="cd2-penalty-label">Financial Exposure</span>
                </div>
              </div>
              <div className="cd2-penalty-card cd2-penalty-card-imprisonment">
                <div className="cd2-penalty-header">
                  <div className="cd2-penalty-left">
                    <div className="cd2-penalty-badge" style={{ background: '#0ea5c1' }}>
                      <img src={iconImprisonment} alt="" />
                    </div>
                    <span className="cd2-penalty-value">0</span>
                    <span className="cd2-penalty-activities">Activities</span>
                  </div>
                </div>
                <div className="cd2-penalty-bottom">
                  <span className="cd2-penalty-label">Imprisonment Risk</span>
                </div>
              </div>
              <div className="cd2-penalty-card cd2-penalty-card-license">
                <div className="cd2-penalty-header">
                  <div className="cd2-penalty-left">
                    <div className="cd2-penalty-badge" style={{ background: '#db2777' }}>
                      <img src={iconLicense} alt="" />
                    </div>
                    <span className="cd2-penalty-value">0</span>
                    <span className="cd2-penalty-activities">Activities</span>
                  </div>
                </div>
                <div className="cd2-penalty-bottom">
                  <span className="cd2-penalty-label">License Revocation</span>
                </div>
              </div>
            </React.Fragment>
          ) : fetchingPenalty || !penaltyMetrics ? (
            <React.Fragment>
              <div className="cd2-penalty-shimmer" />
              <div className="cd2-penalty-shimmer" />
              <div className="cd2-penalty-shimmer" />
            </React.Fragment>
          ) : (
            <React.Fragment>

              {/* ── Shared arrow SVG ── */}
              {/* Financial Exposure — purple */}
              <div
                className="cd2-penalty-card cd2-penalty-card-financial cd2-penalty-card-clickable"
                onClick={() => handlePenaltyClick('financial_exposure')}
                title="Click to view activities with financial exposure"
              >
                <span className="cd2-hover-arrow">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M12 11.3333C12 11.7015 11.7015 12 11.3333 12C10.9651 12 10.6667 11.7015 10.6667 11.3333V6.27604L5.13802 11.8047C4.87767 12.065 4.45566 12.065 4.19531 11.8047C3.93496 11.5443 3.93496 11.1223 4.19531 10.862L9.72396 5.33333H4.66667C4.29848 5.33333 4 5.03486 4 4.66667C4 4.29848 4.29848 4 4.66667 4H11.3333C11.7015 4 12 4.29848 12 4.66667V11.3333Z" fill="#2C2C2C"/>
                  </svg>
                </span>
                {/* Header: badge + value + trend pill (RIGHT) when calendar is closed */}
                <div className="cd2-penalty-header">
                  <div className="cd2-penalty-left">
                    <div className="cd2-penalty-badge" style={{ background: '#7c3aed' }}>
                      <img src={iconFinancialExposure} alt="" />
                    </div>
                    <span className="cd2-penalty-value">
                      {penaltyMetrics.financial_exposure
                        ? formatPenaltyAmount(penaltyMetrics.financial_exposure.total_penalty_amount)
                        : 0}
                    </span>
                  </div>
                  {!calendarOpen && (
                    <span className="cd2-penalty-trend" style={{ color: '#7c3aed' }}>
                      {penaltyMetrics.financial_exposure
                        ? formatPenaltyAvg(penaltyMetrics.financial_exposure.avg_penalty_per_activity)
                        : 0}
                    </span>
                  )}
                </div>
                {/* Bottom: label always + trend pill BELOW when calendar is open */}
                <div className="cd2-penalty-bottom">
                  <span className="cd2-penalty-label">Financial Exposure</span>
                  {calendarOpen && (
                    <span className="cd2-penalty-trend" style={{ color: '#7c3aed' }}>
                      {penaltyMetrics.financial_exposure
                        ? formatPenaltyAvg(penaltyMetrics.financial_exposure.avg_penalty_per_activity)
                        : 0}
                    </span>
                  )}
                </div>
              </div>

              {/* Imprisonment Risk — cyan */}
              <div
                className="cd2-penalty-card cd2-penalty-card-imprisonment cd2-penalty-card-clickable"
                onClick={() => handlePenaltyClick('imprisonment_risk')}
                title="Click to view activities with imprisonment risk"
              >
                <span className="cd2-hover-arrow">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M12 11.3333C12 11.7015 11.7015 12 11.3333 12C10.9651 12 10.6667 11.7015 10.6667 11.3333V6.27604L5.13802 11.8047C4.87767 12.065 4.45566 12.065 4.19531 11.8047C3.93496 11.5443 3.93496 11.1223 4.19531 10.862L9.72396 5.33333H4.66667C4.29848 5.33333 4 5.03486 4 4.66667C4 4.29848 4.29848 4 4.66667 4H11.3333C11.7015 4 12 4.29848 12 4.66667V11.3333Z" fill="#2C2C2C"/>
                  </svg>
                </span>
                <div className="cd2-penalty-header">
                  <div className="cd2-penalty-left">
                    <div className="cd2-penalty-badge" style={{ background: '#0ea5c1' }}>
                      <img src={iconImprisonment} alt="" />
                    </div>
                    <span className="cd2-penalty-value">
                      {penaltyMetrics.imprisonment_risk ? penaltyMetrics.imprisonment_risk.count : 0}
                    </span>
                    <span className="cd2-penalty-activities">Activities</span>
                  </div>
                  {!calendarOpen && (
                    <span className="cd2-penalty-trend" style={{ color: '#0ea5c1' }}>
                      {penaltyMetrics.imprisonment_risk
                        ? penaltyMetrics.imprisonment_risk.pct_of_non_compliant + '% of non-compliant activities'
                        : 0}
                    </span>
                  )}
                </div>
                <div className="cd2-penalty-bottom">
                  <span className="cd2-penalty-label">Imprisonment Risk</span>
                  {calendarOpen && (
                    <span className="cd2-penalty-trend" style={{ color: '#0ea5c1' }}>
                      {penaltyMetrics.imprisonment_risk
                        ? penaltyMetrics.imprisonment_risk.pct_of_non_compliant + '% of non-compliant activities'
                        : 0}
                    </span>
                  )}
                </div>
              </div>

              {/* License Revocation — pink */}
              <div
                className="cd2-penalty-card cd2-penalty-card-license cd2-penalty-card-clickable"
                onClick={() => handlePenaltyClick('license_revocation')}
                title="Click to view activities with license revocation risk"
              >
                <span className="cd2-hover-arrow">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M12 11.3333C12 11.7015 11.7015 12 11.3333 12C10.9651 12 10.6667 11.7015 10.6667 11.3333V6.27604L5.13802 11.8047C4.87767 12.065 4.45566 12.065 4.19531 11.8047C3.93496 11.5443 3.93496 11.1223 4.19531 10.862L9.72396 5.33333H4.66667C4.29848 5.33333 4 5.03486 4 4.66667C4 4.29848 4.29848 4 4.66667 4H11.3333C11.7015 4 12 4.29848 12 4.66667V11.3333Z" fill="#2C2C2C"/>
                  </svg>
                </span>
                <div className="cd2-penalty-header">
                  <div className="cd2-penalty-left">
                    <div className="cd2-penalty-badge" style={{ background: '#db2777' }}>
                      <img src={iconLicense} alt="" />
                    </div>
                    <span className="cd2-penalty-value">
                      {penaltyMetrics.license_revocation ? penaltyMetrics.license_revocation.count : 0}
                    </span>
                    <span className="cd2-penalty-activities">Activities</span>
                  </div>
                  {!calendarOpen && (
                    <span className="cd2-penalty-trend" style={{ color: '#db2777' }}>
                      {penaltyMetrics.license_revocation
                        ? penaltyMetrics.license_revocation.pct_of_non_compliant + '% of non-compliant activities'
                        : 0}
                    </span>
                  )}
                </div>
                <div className="cd2-penalty-bottom">
                  <span className="cd2-penalty-label">License Revocation</span>
                  {calendarOpen && (
                    <span className="cd2-penalty-trend" style={{ color: '#db2777' }}>
                      {penaltyMetrics.license_revocation
                        ? penaltyMetrics.license_revocation.pct_of_non_compliant + '% of non-compliant activities'
                        : 0}
                    </span>
                  )}
                </div>
              </div>

            </React.Fragment>
          )}
        </div>

      </div>

      {/* ── Score cards — 8 KPIs ── */}
      <div className="cd2-stats-row">
        {/* Compliance Rate → Approved → green */}
        <StatCard
          iconSrc={iconComplianceRate}
          value={sc ? `${(sc.compliance_rate && sc.compliance_rate.pct) || 0}%` : '0%'}
          label="Compliance Rate"
          delta={sc && sc.compliance_rate ? sc.compliance_rate.delta_pct : null}
          loading={fetchingScoreCards}
          wide
          tooltip="Percentage of activities completed on time out of all assigned activities in the selected period."
          tipSide="right"
          colorTheme="green"
          onClick={() => handleKpiClick('Compliance Rate', 'Approved')}
        />
        {/* Total Activities → no status filter → blue */}
        <StatCard
          iconSrc={iconTotal}
          value={sc ? ((sc.total_activities && sc.total_activities.count) || 0).toLocaleString() : '0'}
          label="Total Activities"
          delta={sc && sc.total_activities ? sc.total_activities.delta_pct : null}
          loading={fetchingScoreCards}
          tooltip="Total number of compliance activities assigned across all categories and locations."
          colorTheme="blue"
          onClick={() => handleKpiClick('Total Activities', null)}
        />
        {/* Upcoming → Due → amber */}
        <StatCard
          iconSrc={iconUpcoming}
          value={sc ? ((sc.due_overdue && sc.due_overdue.count) || 0).toLocaleString() : '0'}
          label="Upcoming"
          delta={sc && sc.due_overdue ? sc.due_overdue.delta_pct : null}
          loading={fetchingScoreCards}
          tooltip="Activities that are due soon but have not yet passed their deadline."
          colorTheme="amber"
          onClick={() => handleKpiClick('Upcoming', 'Due')}
        />
        {/* Non-Compliant → NonCompliant → red */}
        <StatCard
          iconSrc={iconNonCompliant}
          value={sc ? ((sc.non_compliant && sc.non_compliant.count) || 0).toLocaleString() : '0'}
          label="Non-Compliant"
          delta={sc && sc.non_compliant ? sc.non_compliant.delta_pct : null}
          loading={fetchingScoreCards}
          tooltip="Activities that have crossed their due date without being completed or submitted."
          colorTheme="red"
          onClick={() => handleKpiClick('Non-Compliant', 'NonCompliant')}
        />
        {/* Compliant → Approved → green */}
        <StatCard
          iconSrc={iconCompliant}
          value={sc ? ((sc.approved && sc.approved.count) || 0).toLocaleString() : '0'}
          label="Compliant"
          loading={fetchingScoreCards}
          tooltip="Activities that have been completed, submitted, and approved successfully."
          colorTheme="green"
          onClick={() => handleKpiClick('Compliant', 'Approved')}
        />
        {/* Rejected → Rejected → brown */}
        <StatCard
          iconSrc={iconRejected}
          value={sc ? ((sc.rejected && sc.rejected.count) || 0).toLocaleString() : '0'}
          label="Rejected"
          loading={fetchingScoreCards}
          tooltip="Activities that were submitted but rejected by the approver and require resubmission."
          colorTheme="brown"
          onClick={() => handleKpiClick('Rejected', 'Rejected')}
        />
        {/* Approval Pending → Submitted → dark */}
        <StatCard
          iconSrc={iconPending}
          value={sc ? ((sc.pending_review && sc.pending_review.count) || 0).toLocaleString() : '0'}
          label="Approval Pending"
          loading={fetchingScoreCards}
          tooltip="Activities that have been completed and submitted, but are awaiting manager approval."
          colorTheme="maroon"
          onClick={() => handleKpiClick('Approval Pending', 'Submitted')}
        />
        {/* Recalled → Recalled → orange */}
        <StatCard
          iconSrc={iconRecalled}
          value={sc ? ((sc.recalled && sc.recalled.count) || 0).toLocaleString() : '0'}
          label="Recalled"
          loading={fetchingScoreCards}
          tooltip="Activities that were recalled by the submitter after submission and are no longer under review."
          colorTheme="orange"
          onClick={() => handleKpiClick('Recalled', 'Recalled')}
        />
      </div>

      {/* ── Charts — single full-width card with toggle ── */}
      <div className="cd2-charts-row">
        <div className="cd2-card">
          {/* 3-column header: title (left) | toggle (center) | legend (right) */}
          <div className="cd2-card-header cd2-chart-header">
            <div>
              <div className="cd2-card-title">Compliance by Category and Location</div>
              <p className="cd2-card-subtitle">Non-compliant vs. upcoming vs. compliant across all categories</p>
            </div>
            <div className="cd2-chart-toggle">
              <div
                className={'cd2-chart-toggle-slider' + (chartView === 'location' ? ' slider-right' : ' slider-left')}
                style={{ transform: chartView === 'location' ? 'translateX(100%)' : 'translateX(0)' }}
              />
              <button
                className={'cd2-chart-toggle-btn' + (chartView === 'category' ? ' active' : '')}
                onClick={() => setChartView('category')}
              >
                By Category
              </button>
              <button
                className={'cd2-chart-toggle-btn' + (chartView === 'location' ? ' active' : '')}
                onClick={() => setChartView('location')}
              >
                By Location
              </button>
            </div>
            {/* Legend */}
            <div className="cd2-chart-legend">
              <span className="cd2-legend-dot"><span style={{ background: '#ec221f' }} />Non-Compliant</span>
              <span className="cd2-legend-dot"><span style={{ background: '#e5a000' }} />Upcoming</span>
              <span className="cd2-legend-dot"><span style={{ background: '#14ae5c' }} />Compliant</span>
            </div>
          </div>

          {chartView === 'category'
            ? <StackedBarChart data={categories} loading={fetchingCategories} onSegClick={handleCategorySegClick} />
            : <LocationChart data={locations} loading={fetchingLocations} onSegClick={handleLocationSegClick} />
          }
        </div>
      </div>

      {/* ── Bottom row — accountability table(s) ── */}
      <div className="cd2-bottom-row">
        <div className="cd2-card cd2-owner-table">
          {isEscalation ? (
            /* Escalation: owner × manager pair accountability (combined table) */
            <>
              <div className="cd2-card-title" style={{ marginBottom: 4 }}>Compliance Accountability</div>
              <p className="cd2-card-subtitle">Activity breakdown by assigned owner and manager</p>
              <PairsAccountability
                data={pairs}
                loading={fetchingPairs}
                onCellClick={handlePairCellClick}
              />
            </>
          ) : isOwner ? (
            /* Owner login: see their managers */
            <>
              <div className="cd2-card-title" style={{ marginBottom: 4 }}>Manager Accountability</div>
              <p className="cd2-card-subtitle">Activity breakdown by assigned manager</p>
              <ManagerAccountability
                data={managers}
                loading={fetchingManagers}
                onCellClick={handleManagerCellClick}
              />
            </>
          ) : (
            /* Manager login: see their owners */
            <>
              <div className="cd2-card-title" style={{ marginBottom: 4 }}>Owner Accountability</div>
              <p className="cd2-card-subtitle">Activity breakdown by assigned owner</p>
              <OwnerAccountability
                data={owners}
                loading={fetchingOwners}
                onCellClick={handleOwnerCellClick}
              />
            </>
          )}
        </div>
      </div>
    </div>

    {/* ── Calendar panel — slides in from right when open ── */}
    {calendarOpen && (
      <ComplianceDashboardCalendar
        key="cal-panel"
        onClose={() => { resetCalendarToToday(); setCalendarOpen(false); }}
        onDateSelect={setCalendarDate}
        selectedDate={calendarDate}
        scheduleItems={calendarItems}
        totalCount={calTotal}
        loadingSchedule={fetchingCal}
        dayData={calDayData}
        onMonthChange={handleCalMonthChange}
        onViewMore={handleCalViewMore}
      />
    )}

    </div>
  );
}
